import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ProjectCustomerService } from "@app/project/services/project-customer.service";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { ErrorToSuppress } from "@app/shared/services/db-crud-helper";
import { emailValid, humanNameValid } from "@app/shared/validators";
import { phoneValid } from "@app/shared/validators/phoneValid";
import { User } from "@app/user/model/user";
import { UserManagementService } from "@app/user/service/user-management.service";
import { Observable, Subject, catchError, map, of, take, takeUntil, throwError } from "rxjs";

@Component({
    selector: "app-primary-customer-info-step",
    templateUrl: "./primary-customer-info-step.component.html",
    styleUrls: ["./primary-customer-info-step.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrimaryCustomerInfoStepComponent implements OnInit, OnDestroy, OnChanges {
    @Input() entityId: number;
    @Input() preservedCustomerInfo: User;
    @Input() isReadOnly = false;
    @Input() isNoUserIgnore = false;
    @Input() isNoCustomerIndicated = false;
    public readonly phoneTypeList = ["Home", "Cell", "Work"];
    public isUserListLoaded$: Observable<boolean>;
    public customers$: Observable<User[]>;
    public form: FormGroup = this.formBuilder.group({
        entityId: [],
        noCustomer: false,
        userObj: ["", Validators.required],
        id: [],
        email: ["", emailValid],
        firstName: ["", [Validators.required, humanNameValid]],
        lastName: ["", [Validators.required, humanNameValid]],
        phone: ["", [Validators.required, phoneValid]],
        phoneType: ["Home", Validators.required],
        additionalPhoneNumbers: this.formBuilder.array([]),
        accountNumber: [""],
        roleId: [1],
        permissions: [],
    });

    public existingCustomer: User;
    private unsubscribe$ = new Subject();
    private readonly emailExistsMessage = "This email is in use by a customer in a different company.";

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly userService: UserManagementService,
        private readonly projectCustomerService: ProjectCustomerService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.form.patchValue({ entityId: this.entityId });
        this.getUserCallState();
        this.customers$ = this.projectCustomerService.subscribeToCustomerChanges$(this.form).pipe(
            takeUntil(this.unsubscribe$),
            map(users => {
                const userObj = this.form.value.userObj;
                const partialEmail = typeof userObj === "string" ? userObj : userObj.email;
                if (!!partialEmail && users.length === 1 && users[0].email.toLowerCase() === partialEmail) {
                    this.existingCustomer = users[0];
                    return null;
                }

                this.existingCustomer = null;
                return users;
            })
        );

        this.fields.noCustomer.valueChanges.subscribe(() => this.resetFormValues());
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
        this.projectCustomerService.unsubscribeFromCustomerChangesSubscription();
    }

    ngOnChanges(changes: NgChanges<PrimaryCustomerInfoStepComponent>) {
        if (changes?.entityId?.currentValue && !changes?.entityId?.firstChange) {
            // if an existing user is selected or emailExists error is set,
            // reset entire form so it can recheck with new company, otherwise just set the entityId
            if (this.fields.id.value || this.fields.email.errors?.["emailExists"]) {
                this.resetFormValues();
            } else {
                this.form.patchValue({ entityId: this.entityId });
            }
        }

        if (changes.preservedCustomerInfo?.currentValue) {
            this.preserveFormvalue(changes.preservedCustomerInfo.currentValue);
        }

        if (changes.isReadOnly?.currentValue) {
            this.form.disable();
        }
    }
    preserveFormvalue(preservedUser: User) {
        if (preservedUser?.id) {
            this.form.patchValue({
                entityId: preservedUser.entityId,
                id: preservedUser.id,
                userObj: { email: preservedUser.email },
                email: preservedUser.email,
                firstName: preservedUser.firstName,
                lastName: preservedUser.lastName,
                phone: preservedUser.phone,
                phoneType: preservedUser.phoneType,
                additionalPhoneNumbers: [
                    {
                        phone: preservedUser.phone2,
                        phoneType: preservedUser.phone2Type,
                    },
                    {
                        phone: preservedUser.phone3,
                        phoneType: preservedUser.phone3Type,
                    },
                ],
                accountNumber: preservedUser.accountNumber,
                roleId: preservedUser.roleId,
                permissions: preservedUser.permissions,
            });

            if (preservedUser.phone2 || preservedUser.phone3) {
                if (preservedUser.phone2) {
                    this.additionalPhoneNumbers.push(
                        this.projectCustomerService.createAdditionalPhoneNumberGroup(preservedUser.phone2 + "", preservedUser.phone2Type)
                    );
                }
                if (preservedUser.phone3) {
                    this.additionalPhoneNumbers.push(
                        this.projectCustomerService.createAdditionalPhoneNumberGroup(preservedUser.phone3 + "", preservedUser.phone3Type)
                    );
                }
            }
        } else if (this.isNoCustomerIndicated) {
            this.form.patchValue({
                noCustomer: true,
            });
        }
    }

    public isValid(): boolean {
        // return this.form.valid;
        return this.isReadOnly || this.fields.noCustomer.value || this.form.valid;
    }

    public getValue(): any {
        const formValue = this.form.getRawValue();
        return {
            accountNumber: formValue.accountNumber,
            email: formValue.email,
            entityId: formValue.entityId,
            firstName: formValue.firstName,
            id: formValue.id,
            lastName: formValue.lastName,
            permissions: formValue.permissions,
            phone: formValue.phone,
            phone2: formValue.additionalPhoneNumbers[0]?.phone || null,
            phone2Type: formValue.additionalPhoneNumbers[0]?.phoneType || null,
            phone3: formValue.additionalPhoneNumbers[1]?.phone || null,
            phone3Type: formValue.additionalPhoneNumbers[1]?.phoneType || null,
            phoneType: formValue.phoneType,
            roleId: formValue.roleId,
            userObj: formValue.userObj,
            noCustomer: formValue.noCustomer,
        };
    }

    public addPhoneNumber(): void {
        this.additionalPhoneNumbers.push(this.projectCustomerService.createAdditionalPhoneNumberGroup("", this.getNextUnusedPhoneType()));
    }

    public removePhoneNumber(index: number): void {
        this.additionalPhoneNumbers.removeAt(index);
    }

    public get fields(): {
        [key: string]: AbstractControl;
    } {
        return this.form.controls;
    }

    public displayFn(user) {
        return user?.email;
    }

    public checkEmailInOtherEntities(): void {
        // run field through email validator before checking other companies
        // if no value returned, it is valid
        if (!emailValid(this.fields.email as FormControl)) {
            this.userService
                .checkForEmailExistence(this.fields.email.value as string)
                .pipe(
                    take(1),
                    map(customer => {
                        if (customer && customer.entityId !== this.fields.entityId.value) {
                            this.setEmailExistsError();
                        }
                    }),
                    catchError((error: DataServiceError<{ error: string }>) => {
                        // permission denied error thrown when user exists, but in a different company
                        if (error.error === ErrorToSuppress.PERMISSION_DENIED_ERROR) {
                            this.setEmailExistsError();
                            return of(null);
                        }
                        return throwError(() => new DataServiceError(error.error));
                    })
                )
                .subscribe();
        }
    }

    private setEmailExistsError(): void {
        this.fields.email.setErrors({
            emailExists: this.emailExistsMessage,
        });
        this.fields.userObj.setErrors({ invalid: true });
        this.cdr.detectChanges();
    }

    private get additionalPhoneNumbers(): FormArray {
        return this.fields.additionalPhoneNumbers as FormArray;
    }

    private getUserCallState(): void {
        this.isUserListLoaded$ = this.userService.isUserListLoaded();
    }

    private getNextUnusedPhoneType(): string {
        for (const type of this.phoneTypeList) {
            if (
                this.fields.phoneType.value !== type &&
                !this.additionalPhoneNumbers.controls.some(c => c.get("phoneType").value === type)
            ) {
                return type;
            }
        }
    }

    private resetFormValues(): void {
        this.form.patchValue({
            entityId: this.entityId,
            userObj: "",
            id: null,
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            phoneType: "Home",
            additionalPhoneNumbers: [],
            acountNumber: "",
            permissions: null,
            roleId: 1,
        });

        this.form.markAsUntouched();
    }
}
